import { useMemo, useRef, useState } from 'react';
import { User, Mail, Lock, CheckCircle, XCircle, AtSign, ArrowLeft, AlertTriangle, FileText, Shield, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { registerUser } from '../../redux/authentication/utils/core/authslice';
import { Input, Button, Modal } from '../../components/ui';
import { useTheme } from '../../theme/ThemeContext';

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

const USERNAME_RE = /^[A-Za-z0-9_]{8,32}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validatePasswordMsg = (p, t) => {
  if (!p) return t('auth.register.errors.passwordRequired');
  if (p.length < 8) return t('auth.register.errors.passwordTooShort');
  if (!/[A-Za-z]/.test(p) || !/[0-9]/.test(p) || !/[^A-Za-z0-9]/.test(p)) {
    return t('auth.register.errors.passwordComplexity');
  }
  return '';
};

// Legal Modal Component -- now built on the shared Modal primitive
// (components/ui/Modal.jsx) instead of its own bespoke backdrop/card shell.
// Same content, same visual shape; gains Escape-to-close and light-mode
// support for free from the primitive.
const LegalModal = ({ type, onClose }) => {
  const { t } = useTranslation();

  const getContent = () => {
    switch (type) {
      case 'privacy':
        return {
          icon: Shield,
          title: t('auth.register.legalModal.privacy.title'),
          content: (
            <div className="space-y-4 text-gray-700 dark:text-gray-300 text-sm">
              <p>{t('auth.register.legalModal.privacy.intro')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.privacy.rightsLabel')}</strong> {t('auth.register.legalModal.privacy.rightsText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.privacy.dataUsageLabel')}</strong> {t('auth.register.legalModal.privacy.dataUsageText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.privacy.dataSharingLabel')}</strong> {t('auth.register.legalModal.privacy.dataSharingText')}</p>
              <p className="text-violet-600 dark:text-violet-400">{t('auth.register.legalModal.privacy.fullDetails')}</p>
            </div>
          )
        };
      case 'terms':
        return {
          icon: FileText,
          title: t('auth.register.legalModal.terms.title'),
          content: (
            <div className="space-y-4 text-gray-700 dark:text-gray-300 text-sm">
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.terms.serviceDescriptionLabel')}</strong> {t('auth.register.legalModal.terms.serviceDescriptionText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.terms.betaAccessLabel')}</strong> {t('auth.register.legalModal.terms.betaAccessText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.terms.acceptableUseLabel')}</strong> {t('auth.register.legalModal.terms.acceptableUseText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.terms.terminationLabel')}</strong> {t('auth.register.legalModal.terms.terminationText')}</p>
              <p className="text-violet-600 dark:text-violet-400">{t('auth.register.legalModal.terms.fullDetails')}</p>
            </div>
          )
        };
      case 'disclaimer':
        return {
          icon: AlertTriangle,
          title: t('auth.register.legalModal.disclaimer.title'),
          content: (
            <div className="space-y-4 text-gray-700 dark:text-gray-300 text-sm">
              <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-orange-600 dark:border-yellow-500 p-3">
                <p className="font-bold text-yellow-700 dark:text-yellow-400">{t('auth.register.legalModal.disclaimer.criticalNotice')}</p>
              </div>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.disclaimer.notInvestmentAdviceLabel')}</strong> {t('auth.register.legalModal.disclaimer.notInvestmentAdviceText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.disclaimer.doYourOwnResearchLabel')}</strong> {t('auth.register.legalModal.disclaimer.doYourOwnResearchText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.disclaimer.risksLabel')}</strong> {t('auth.register.legalModal.disclaimer.risksText')}</p>
              <p><strong className="text-gray-900 dark:text-white">{t('auth.register.legalModal.disclaimer.noGuaranteesLabel')}</strong> {t('auth.register.legalModal.disclaimer.noGuaranteesText')}</p>
              <p className="text-red-600 dark:text-red-400">{t('auth.register.legalModal.disclaimer.acceptRisks')}</p>
            </div>
          )
        };
      default:
        return { icon: FileText, title: '', content: null };
    }
  };

  const { icon: Icon, title, content } = getContent();

  const handleViewFullDocument = () => {
    const routes = {
      privacy: '/privacy',
      terms: '/terms',
      disclaimer: '/disclaimer'
    };
    window.open(routes[type], '_blank');
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      icon={Icon}
      footer={
        <>
          <Button variant="primary" fullWidth onClick={onClose}>{t('common.close')}</Button>
          <Button variant="ghost" fullWidth onClick={handleViewFullDocument}>{t('auth.register.legalModal.readFullDocument')}</Button>
        </>
      }
    >
      {content}
    </Modal>
  );
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDark, toggleTheme } = useTheme();

  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Honeypot: real users never see or fill this (off-screen, no label a
  // screen-reader would announce as a real field); a non-empty value here
  // means whatever submitted the form is a bot, not a person.
  const [honeypot, setHoneypot] = useState('');

  // hCaptcha
  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState('');

  // 2026-08-11: same fix as LoginPage.jsx's submitInFlightRef -- this button
  // is only disabled via isLoading (React state, needs a re-render to take
  // effect), leaving a real window for a fast double-click to fire
  // handleSubmit twice. The second call would hit authService.js's shared
  // register() -> _checkOperationState('register') single-flight guard and
  // throw a client-side "already in progress" message that this page's own
  // catch-all error branch would otherwise show as a generic submit error.
  // A ref (not state) is required -- it updates synchronously.
  const submitInFlightRef = useRef(false);

  // Legal acceptance states
  const [legalAcceptance, setLegalAcceptance] = useState({
    terms: false,
    privacy: false,
    disclaimer: false
  });
  const [showLegalModal, setShowLegalModal] = useState(null);

  // Password strength meter
  const passwordStrength = useMemo(() => {
    const p = formData.password || '';
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[a-z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  }, [formData.password]);

  const getStrengthColor = () =>
    passwordStrength <= 2 ? 'bg-red-500' : passwordStrength <= 3 ? 'bg-yellow-500' : 'bg-green-500';

  const getStrengthText = () =>
    passwordStrength <= 2 ? t('auth.register.passwordStrength.weak') : passwordStrength <= 3 ? t('auth.register.passwordStrength.medium') : t('auth.register.passwordStrength.strong');

  // Live validation
  const handleInputChange = (ev) => {
    const { name, value } = ev.target;
    const next = { ...formData, [name]: value };
    setFormData(next);

    if (name === 'username') {
      const u = value.trim();
      const msg = !u
        ? t('auth.register.errors.usernameRequired')
        : !USERNAME_RE.test(u)
        ? t('auth.register.errors.usernameFormat')
        : '';
      setErrors((prev) => ({ ...prev, username: msg }));
    }

    if (name === 'email') {
      const e = value.trim();
      const msg = !e ? t('auth.register.errors.emailRequired') : !EMAIL_RE.test(e) ? t('auth.register.errors.emailInvalid') : '';
      setErrors((prev) => ({ ...prev, email: msg }));
    }

    if (name === 'password') {
      setErrors((prev) => ({ ...prev, password: validatePasswordMsg(value, t) }));
      const confirmMsg = next.confirmPassword !== value ? t('auth.register.errors.passwordsMismatch') : '';
      setErrors((prev) => ({ ...prev, confirmPassword: confirmMsg }));
    }

    if (name === 'confirmPassword') {
      const msg = value !== next.password ? t('auth.register.errors.passwordsMismatch') : '';
      setErrors((prev) => ({ ...prev, confirmPassword: msg }));
    }
  };

  // Handle legal checkbox changes
  const handleLegalCheck = (type) => {
    setLegalAcceptance(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    if (errors.legal) {
      setErrors(prev => ({ ...prev, legal: '' }));
    }
  };

  // Final validation
  const validateForm = () => {
    const e = {};
    const u = formData.username.trim();
    if (!u) e.username = t('auth.register.errors.usernameRequired');
    else if (!USERNAME_RE.test(u)) e.username = t('auth.register.errors.usernameFormat');

    const em = formData.email.trim();
    if (!em) e.email = t('auth.register.errors.emailRequired');
    else if (!EMAIL_RE.test(em)) e.email = t('auth.register.errors.emailInvalid');

    if (!formData.firstName.trim()) e.firstName = t('auth.register.errors.firstNameRequired');
    if (!formData.lastName.trim()) e.lastName = t('auth.register.errors.lastNameRequired');

    const pMsg = validatePasswordMsg(formData.password, t);
    if (pMsg) e.password = pMsg;

    if (formData.password !== formData.confirmPassword) e.confirmPassword = t('auth.register.errors.passwordsMismatch');

    // Validate legal acceptance
    if (!legalAcceptance.terms || !legalAcceptance.privacy || !legalAcceptance.disclaimer) {
      e.legal = t('auth.register.errors.legalRequired');
    }

    if (HCAPTCHA_SITE_KEY && !captchaToken) {
      e.captcha = t('auth.register.errors.captchaRequired');
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (submitInFlightRef.current) return; // silently ignore a near-simultaneous duplicate submit

    submitInFlightRef.current = true;
    setIsLoading(true);
    try {
      // Only send what backend requires for UserCreate
      const payload = {
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        // Optionally include: full_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`
        h_captcha_response: captchaToken,
        middle_name: honeypot,
      };

      const action = await dispatch(registerUser(payload));

      if (action.error) {
        // A bot-check rejection invalidates the token either way -- reset
        // the widget so a real user stuck behind a false positive (or a
        // second submit attempt) gets a fresh challenge instead of silently
        // resubmitting an already-consumed one.
        captchaRef.current?.resetCaptcha();
        setCaptchaToken('');

        const rawMsg = action.payload || action.error.message || '';
        console.warn('Registration error', rawMsg, action); // dev-only

        // 1) Field-specific conflicts from backend text
        if (/email.*(registered|exists)/i.test(rawMsg)) {
          setErrors(prev => ({ ...prev, email: t('auth.register.errors.emailTaken') }));
        } else if (/username.*(taken|exists)/i.test(rawMsg)) {
          setErrors(prev => ({ ...prev, username: t('auth.register.errors.usernameTaken') }));
        }
        // 2) Generic conflict (409) without clear field
        else if (/409|conflict/i.test(rawMsg)) {
          setErrors(prev => ({
            ...prev,
            submit: t('auth.register.errors.accountConflict')
          }));
        }
        // 3) Network / timeout cases
        else if (/network|timeout/i.test(rawMsg)) {
          setErrors(prev => ({
            ...prev,
            submit: t('auth.register.errors.networkError')
          }));
        }
        // 4a) authService.js's own client-side single-flight/1s-cooldown
        // guard -- a client-side timing issue, not a real submission
        // failure. submitInFlightRef above should prevent the common
        // trigger before it ever reaches here; this is defense in depth.
        else if (/operation already in progress|wait before attempting/i.test(rawMsg)) {
          setErrors(prev => ({
            ...prev,
            submit: t('auth.register.errors.submitInProgress')
          }));
        }
        // 4b) Catch-all
        else {
          setErrors(prev => ({
            ...prev,
            submit: t('auth.register.errors.genericSubmitError')
          }));
        }

        return;
      }

      // Success: backend does not log in on register → redirect to login
      navigate('/login?registered=1', { replace: true });
    } finally {
      setIsLoading(false);
      submitInFlightRef.current = false;
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 relative overflow-hidden bg-gray-50 dark:bg-[#09090f]"
      style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>

      {/* Top radial glow — identical to the homepage hero. Dark-only by
          design, same reasoning as LoginPage.jsx. */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[480px] pointer-events-none hidden dark:block"
        style={{ background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)' }}
      />

      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center text-violet-600 dark:text-violet-300 hover:text-violet-500 dark:hover:text-violet-100 transition z-50"
      >
        <ArrowLeft className="w-5 h-5 mr-1" /> {t('auth.back')}
      </button>

      <button
        onClick={toggleTheme}
        className="absolute top-6 right-6 p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors z-50"
        title={isDark ? t('auth.switchToLight') : t('auth.switchToDark')}
        aria-label={isDark ? t('auth.switchToLight') : t('auth.switchToDark')}
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Legal Modal */}
      {showLegalModal && (
        <LegalModal
          type={showLegalModal}
          onClose={() => setShowLegalModal(null)}
        />
      )}

      <div className="relative w-full max-w-md z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img
              src="/StockerLogo.png"
              alt="Stocker"
              className="h-16 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center shadow-2xl" style="background:#7c3aed"><span class="text-white font-bold text-2xl">S</span></div>';
              }}
            />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.register.heading')}</h2>
          <p className="text-gray-600 dark:text-gray-400">{t('auth.register.subtitle')}</p>
        </div>

        {/* Form */}
        <div className="rounded-3xl p-5 sm:p-8 shadow-2xl bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]">
          <div className="space-y-6">
            <Input
              icon={AtSign}
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder={t('auth.register.placeholders.username')}
              autoComplete="username"
              required
              error={errors.username}
            />

            {/* Names */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                icon={User}
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={t('auth.register.placeholders.firstName')}
                required
                error={errors.firstName}
              />
              <Input
                icon={User}
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder={t('auth.register.placeholders.lastName')}
                required
                error={errors.lastName}
              />
            </div>

            <Input
              icon={Mail}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('auth.register.placeholders.email')}
              autoComplete="email"
              required
              error={errors.email}
            />

            {/* Password */}
            <div>
              <Input
                icon={Lock}
                isPassword
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('auth.register.placeholders.password')}
                autoComplete="new-password"
                required
                error={errors.password}
              />

              {formData.password && (
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('auth.register.passwordStrengthLabel')}</span>
                    <span
                      className={`text-xs font-medium ${
                        passwordStrength <= 2 ? 'text-red-600 dark:text-red-400' : passwordStrength <= 3 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
                      }`}
                    >
                      {getStrengthText()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Input
                icon={Lock}
                isPassword
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={t('auth.register.placeholders.confirmPassword')}
                autoComplete="new-password"
                required
                error={errors.confirmPassword}
              />
              {formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && (
                <p className="text-green-600 dark:text-green-400 text-sm mt-1 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" /> {t('auth.register.passwordsMatchConfirmation')}
                </p>
              )}
            </div>

            {/* Legal Acceptance - REQUIRED */}
            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-white/10">
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('auth.register.legalAgreementsHeading')}</p>

              {/* Terms of Service */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={legalAcceptance.terms}
                  onChange={() => handleLegalCheck('terms')}
                  className="mt-1 w-5 h-5 rounded border-2 border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 checked:bg-violet-600 checked:border-violet-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                  {t('auth.register.agreeToThe')}{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLegalModal('terms');
                    }}
                    className="text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300 underline font-medium"
                  >
                    {t('auth.register.legalModal.terms.title')}
                  </button>
                </span>
              </label>

              {/* Privacy Policy */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={legalAcceptance.privacy}
                  onChange={() => handleLegalCheck('privacy')}
                  className="mt-1 w-5 h-5 rounded border-2 border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 checked:bg-violet-600 checked:border-violet-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                  {t('auth.register.acknowledgeThe')}{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLegalModal('privacy');
                    }}
                    className="text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300 underline font-medium"
                  >
                    {t('auth.register.legalModal.privacy.title')}
                  </button>
                </span>
              </label>

              {/* Investment Disclaimer */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={legalAcceptance.disclaimer}
                  onChange={() => handleLegalCheck('disclaimer')}
                  className="mt-1 w-5 h-5 rounded border-2 border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 checked:bg-violet-600 checked:border-violet-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                  {t('auth.register.understandAndAccept')}{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLegalModal('disclaimer');
                    }}
                    className="text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300 underline font-medium"
                  >
                    {t('auth.register.legalModal.disclaimer.title')}
                  </button>
                </span>
              </label>

              {errors.legal && (
                <p className="text-red-600 dark:text-red-400 text-sm flex items-center">
                  <XCircle className="w-4 h-4 mr-1" /> {errors.legal}
                </p>
              )}
            </div>

            {/* Honeypot -- off-screen (not type="hidden", which bots skip),
                no visible label, excluded from tab order and screen readers.
                A real user never sees or fills this. */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
            >
              <label htmlFor="middle_name">{t('auth.register.honeypotLabel')}</label>
              <input
                id="middle_name"
                name="middle_name"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>

            {/* Bot-challenge -- see RegisterPage.jsx's HCAPTCHA_SITE_KEY /
                REACT_APP_HCAPTCHA_SITE_KEY for the dev-vs-live key story. */}
            {HCAPTCHA_SITE_KEY && (
              <div className="flex flex-col items-center gap-2">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITE_KEY}
                  onVerify={(token) => {
                    setCaptchaToken(token);
                    setErrors((prev) => ({ ...prev, captcha: '' }));
                  }}
                  onExpire={() => setCaptchaToken('')}
                  onError={() => setCaptchaToken('')}
                />
                {errors.captcha && (
                  <p className="text-red-600 dark:text-red-400 text-sm flex items-center">
                    <XCircle className="w-4 h-4 mr-1" /> {errors.captcha}
                  </p>
                )}
              </div>
            )}

            {/* Submit */}
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              loading={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? t('auth.register.submitting') : t('auth.register.submit')}
            </Button>

            {errors.submit && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-600/30 border border-red-300 dark:border-red-500 text-red-700 dark:text-red-200 text-sm text-center flex items-center justify-center">
                <XCircle className="w-4 h-4 mr-2" />
                {errors.submit}
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              {t('auth.register.alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300 font-medium transition-colors"
              >
                {t('auth.register.signInHere')}
              </button>
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
          {t('auth.register.legalFooterNote')}
        </p>
      </div>
    </div>
  );
}